import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import AvailabilityTemplatesSection from "./AvailabilityTemplatesSection";
import AvailabilityExceptionsSection from "./AvailabilityExceptionsSection";
import Button from "../../../../components/controls/Button";
import { RecurrenceInfoSection } from "../RecurrenceInfoSection";

export default function AvailabilityModal({ isOpen, onClose, item }) {
  const [activeTab, setActiveTab] = useState("templates");

  const [templates, setTemplates] = useState([]);
  const [exceptions, setExceptions] = useState([]);

  const [collapsedDays, setCollapsedDays] = useState({
    0: true,
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
  });

  const dayNames = {
    0: "Dimanche",
    1: "Lundi",
    2: "Mardi",
    3: "Mercredi",
    4: "Jeudi",
    5: "Vendredi",
    6: "Samedi",
  };

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingExceptions, setLoadingExceptions] = useState(false);

  const [recurrenceWeeks, setRecurrenceWeeks] = useState(6);

  useEffect(() => {
    if (!isOpen || !item) return;

    loadTemplates();
    loadExceptions();
    setRecurrenceWeeks(item.recurrenceMaxWeeks || 8);
  }, [isOpen, item]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/services/${item.id}/availability-templates`
      );

      if (!res.ok) throw new Error("Erreur chargement templates");

      const data = await res.json();

      const grouped = [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
        dayOfWeek,
        slots: data
          .filter((t) => t.dayOfWeek === dayOfWeek)
          .map((t) => ({
            id: t.id,
            startTime: t.startTime,
            endTime: t.endTime,
            maxBookings: t.maxBookings,
            priceOverride: t.priceOverride,
            validFrom: t.validFrom,
            validUntil: t.validUntil,
            isActive: t.isActive,
          })),
      }));

      setTemplates(grouped);
    } catch (err) {
      console.error(err);
    }

    setLoadingTemplates(false);
  };

  const loadExceptions = async () => {
    setLoadingExceptions(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/services/${item.id}/exceptions`
      );

      if (!res.ok) {
        setExceptions([]);
        setLoadingExceptions(false);
        return;
      }

      const data = await res.json();
      setExceptions(data);
    } catch (err) {
      console.error(err);
    }

    setLoadingExceptions(false);
  };

  const toggleDay = (d) =>
    setCollapsedDays((prev) => ({ ...prev, [d]: !prev[d] }));

  const addSlot = (dayOfWeek) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              slots: [
                ...day.slots,
                {
                  startTime: "09:00",
                  endTime: "17:00",
                  maxBookings: 1,
                  priceOverride: null,
                  validFrom: null,
                  validUntil: null,
                  isActive: true,
                },
              ],
            }
      )
    );
  };

  const updateSlot = (dayOfWeek, index, field, value) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              slots: day.slots.map((slot, i) =>
                i === index ? { ...slot, [field]: value } : slot
              ),
            }
      )
    );
  };

  const removeSlot = (dayOfWeek, index) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : { ...day, slots: day.slots.filter((_, i) => i !== index) }
      )
    );
  };

  // ---------- EXCEPTIONS ----------
  const addException = () => {
    setExceptions((prev) => [
      ...prev,
      {
        id: null, // new
        startDateTime: null,
        endDateTime: null,
        reason: "",
        isNew: true,
      },
    ]);
  };

  const updateException = (index, field, value) => {
    setExceptions((prev) =>
      prev.map((ex, i) => (i === index ? { ...ex, [field]: value } : ex))
    );
  };

  const deleteException = async (index, id) => {
    const ex = exceptions[index];

    // Local only
    if (ex.isNew) {
      setExceptions((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    // Delete API
    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/services/${item.id}/exceptions/${id}`,
      { method: "DELETE" }
    );

    setExceptions((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- SAVE ALL ----------
  const handleSave = async () => {
    // Save recurrence
    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/${item.id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recurrenceMaxWeeks: recurrenceWeeks }),
      }
    );

    // Save templates
    const flatTemplates = templates.flatMap((day) =>
      day.slots.map((slot) => ({
        dayOfWeek: day.dayOfWeek,
        startTime: slot.startTime,
        endTime: slot.endTime,
        maxBookings: slot.maxBookings,
        priceOverride: slot.priceOverride,
        validFrom: slot.validFrom,
        validUntil: slot.validUntil,
        isActive: true,
      }))
    );

    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/services/${item.id}/availability-templates`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(flatTemplates),
      }
    );

    // Save exceptions
    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Inventory/services/${item.id}/exceptions`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          exceptions.map((ex) => ({
            id: ex.id,
            startDateTime: ex.startDateTime,
            endDateTime: ex.endDateTime,
            reason: ex.reason ?? null,
          }))
        ),
      }
    );

    item.recurrenceMaxWeeks = recurrenceWeeks;

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-5xl w-full p-0 flex flex-col max-h-[90vh] min-h-90vh h-full rounded-xl">
        {/* HEADER */}
        <div className="sticky top-0 bg-white z-30 border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">Disponibilités – {item?.name}</h3>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* TABS LIFT */}
        <div className="sticky top-[64px] bg-white z-20 px-6 py-3">
          <div role="tablist" className="tabs tabs-lift tabs-md">
            <a
              role="tab"
              className={`tab ${activeTab === "templates" ? "tab-active" : ""}`}
              onClick={() => setActiveTab("templates")}
            >
              Disponibilités
            </a>

            <a
              role="tab"
              className={`tab ${
                activeTab === "exceptions" ? "tab-active" : ""
              }`}
              onClick={() => setActiveTab("exceptions")}
            >
              Exclusions
            </a>
            <a
              role="tab"
              className={`tab ${
                activeTab === "recurrence" ? "tab-active" : ""
              }`}
              onClick={() => setActiveTab("recurrence")}
            >
              Récurrence
            </a>
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {activeTab === "templates" &&
            (templates === null ? (
              <div className="text-center py-10">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <AvailabilityTemplatesSection
                templates={templates}
                collapsedDays={collapsedDays}
                onToggleDay={toggleDay}
                onAddSlot={addSlot}
                onRemoveSlot={removeSlot}
                onUpdateSlot={updateSlot}
                loading={loadingTemplates}
                dayNames={dayNames}
              />
            ))}

          {activeTab === "recurrence" && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">
                Récurrence des disponibilités
              </h3>

              <p className="text-gray-600">
                Nombre de semaines utilisées pour générer les disponibilités
                futures.
              </p>

              <div className="flex items-center gap-3">
                <label className="font-medium">Nombre de semaines :</label>
                <input
                  type="number"
                  min={1}
                  className="input input-bordered w-32"
                  value={recurrenceWeeks}
                  onChange={(e) => setRecurrenceWeeks(parseInt(e.target.value))}
                />
              </div>

              <RecurrenceInfoSection></RecurrenceInfoSection>
            </div>
          )}

          {activeTab === "exceptions" && (
            <AvailabilityExceptionsSection
              exceptions={exceptions}
              loading={loadingExceptions}
              onAddException={addException}
              onUpdateException={updateException}
              onDeleteException={deleteException}
            />
          )}
        </div>

        {/* FOOTER */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-3">
          <Button color="coral" size="sm" onClick={onClose}>
            Annuler
          </Button>

          <Button color="primary" size="sm" onClick={handleSave}>
            Enregistrer
          </Button>
        </div>
      </div>

      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
}
